import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import TagManager from "react-gtm-module";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { COMPANY_OPTIONS } from "../../../constants";
import { useAxios, useToggle } from "../../../hooks";
import { CreateBidValidationSchema } from "@next-pontos/validations";
import { Program } from "../new-bid-form-step-one";
import currency from "currency.js";
import { orderBy } from "lodash";

export const useNewBidData = () => {
  const [bid, createBid] = useAxios({ url: "/bid", method: "POST" }, { manual: true });
  const [dashboard] = useAxios("/user/dashboard");
  const [programs] = useAxios("/program");
  const [accounts, refetchAccounts] = useAxios("/account");
  const [activeStep, setActiveStep] = useState(1);
  const [validationSchema, setValidationSchema] = useState<any>();
  const [createAccountModalVisibility, createAccountModalVisibilityControls] = useToggle(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const leadBidId = searchParams.get('lead_bid_id')
  const [leadBid, fetchLeadBid] = useAxios(`/lead/bid/${leadBidId}`, {
    manual: true
  });

  const [ranking, fetchRanking] = useAxios(`/api/public/bid/ranking`, {
    manual: true
  });

  const getValidationSchema = () => {
    const company = formik.values.company;
    const program = programs.data?.find((p: Program) => p.company === company);

    switch (activeStep) {
      case 1:
        return setValidationSchema(Yup.object({
          company: CreateBidValidationSchema.fields.company,
          account_id: CreateBidValidationSchema.fields.account_id,
          availability_days: CreateBidValidationSchema.fields.availability_days,
          availability_hours: CreateBidValidationSchema.fields.availability_hours,
          needs_email_redirect: Yup.boolean().when("company", (company, schema) => 
            program?.needs_email_redirect ? schema.oneOf([true], "O redirecionamento de e-mail deve ser confirmado.") : schema
          ),
        }));
      case 2:
        return setValidationSchema(Yup.object({
          miles: Yup.number().when("company", (company, schema) => {
            const minAmount = program?.min_amount || 0;
            return schema.min(minAmount, `O mínimo para esta companhia é ${Number(minAmount).toLocaleString("pt-BR")} milhas`)
                        .required("Este campo é obrigatório")
                        .test("greater-than-zero", "Deve ser maior que zero", (value) => Number(value) > 0);
          }),
          price: CreateBidValidationSchema.fields.price,
          pax: CreateBidValidationSchema.fields.pax,
        }));
      default:
        return setValidationSchema(CreateBidValidationSchema);
    }
  };
  
  const formik = useFormik({
    initialValues: {
      company: leadBid.data?.company || "",
      miles: leadBid.data?.amount || "",
      price: leadBid.data?.price_per_k || "",
      pax: "",
      account_id: "",
      accepts_custom_payment: false,
      needs_email_redirect: false,
      availability_days: {
        friday: false,
        monday: false,
        saturday: false,
        sunday: false,
        thursday: false,
        tuesday: false,
        wednesday: false
      },
      availability_hours: {
        afternoon: false,
        evening: false,
        morning: false
      },
    },
    onSubmit: async (values) => {
      TagManager.dataLayer({
        dataLayer: {
          event: "SubmitOffer",
          company: values.company,
          miles: values.miles,
          price: values.price,
        },
      });
      await createBid({ data: values });
      navigate("/minhas-ofertas");
    },
    validationSchema,
    validateOnMount: true,
  });

  useEffect(() => {
    if (!leadBid.data && !leadBid.loading && leadBidId) {
      fetchLeadBid().then((data) => {
        const result = data.data as { company: string, amount: string, price_per_k: string }

        formik.setFieldValue('company', result?.company || "") 
        formik.setFieldValue('miles', result?.amount || "") 
        formik.setFieldValue('price', currency(result?.price_per_k).value || "") 
      })
    }
  }, [leadBidId])
  
  useEffect(() => {
    getValidationSchema();
  }, [activeStep, formik.values.company, programs.data]);

  const accountOptions = useMemo(() => (
    orderBy((accounts.data || [])
      .filter((item: { company: string }) => item.company === formik.values.company)
      .map((item: { company: string; holder_name: string; id: number }) => ({
        label: `${COMPANY_OPTIONS.find((c) => c.value === item.company)?.label} ${item.holder_name}`,
        value: item.id,
      })), 'label')
  ), [accounts.data, formik.values.company]);

  const programPriceSettings = useMemo(() => {
    return programs?.data?.find((item: { company: string; program_price_settings: { direction: string; }[] }) => 
      item.company === formik.values.company)?.program_price_settings.filter((item: { direction: string; }) => item.direction === "BUY") || [];
  }, [formik.values.company, programs.data]);

  useEffect(() => {
    if (formik.values.company) {
      fetchRanking({
        params: {
          company: formik.values.company
        }
      })
    }
  }, [formik.values.company])

  useEffect(() => {
    formik.setFieldValue(
      "accepts_custom_payment",
      formik.values.company === "IBERIA"
    );
  }, [formik.values.company])

  return {
    bid,
    dashboard,
    programs,
    accounts,
    activeStep,
    setActiveStep,
    formik,
    createAccountModalVisibilityControls,
    createAccountModalVisibility,
    accountOptions,
    programPriceSettings,
    refetchAccounts,
    ranking: ranking.data
  };
};
